import { api } from "_shared/_stores/api";
import { AvatarCrop } from "marketplace/_views/avatars/avatar-crop";
import { AvatarSize } from "marketplace/_views/avatars/avatar-size";
import TeamSchema, { TeamJSON } from "marketplace/teams/_schema/team-schema";
import MarketplaceTeamAPIRequestSchema from "marketplace/teams/_api/_schemas/marketplace-team-api-request-schema";
import MarketplaceTeamSearchSchema, { MarketplaceTeamSearchJSON } from "marketplace/teams/_api/_schemas/marketplace-team-search-schema";
import { buildUpdateMarketplaceTeamPayload } from "marketplace/teams/_api/requests-contracts/update-marketplace-team-api-request-schema";
import TeamInvitationSchema, { TeamInvitationJSON } from "marketplace/teams/invitations/_schemas/team-invitation/team-invitation-schema";
import TeamOpenToInvitationSchema, { TeamOpenToInvitationJSON } from "marketplace/teams/invitations/_schemas/team-open-to-invitation/team-open-to-invitation-schema";
import Team from "marketplace/teams/team";
import ForumSchema, { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import z from "zod";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";

export const marketplaceTeamsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Queries
    getTeamVendors: build.query<TeamJSON[], void>({
      query: () => "marketplace/team-vendors",
      rawResponseSchema: z.array(TeamSchema.TeamJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: [{ type: "Team" as const, id: "LIST" }],
    }),

    getTeamById: build.query<TeamJSON, { teamId: string }>({
      query: ({ teamId }) => `marketplace/teams/${teamId}`,
      rawResponseSchema: TeamSchema.TeamJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) =>
        result ? [{ type: "Team" as const, id: result.id }] : [{ type: "Team" as const, id: "LIST" }],
    }),

    getTeamsByIds: build.query<TeamJSON[], { ids: string[] }>({
      async queryFn({ ids }, _api, _extraOptions, fetchWithBQ) {
        if (!ids.length) return { data: [] };
        const result = await fetchWithBQ({
          url: "marketplace/teams-by-ids",
          method: "POST",
          body: ids,
          headers: { "Content-Type": "application/json" },
        });
        if (result.error) return { error: result.error };
        return { data: result.data as TeamJSON[] };
      },
      responseSchema: z.array(TeamSchema.TeamJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Team" as const, id })),
              { type: "Team" as const, id: "LIST" },
            ]
          : [{ type: "Team" as const, id: "LIST" }],
    }),

    searchTeamsByQuery: build.query<TeamJSON[], { query: MarketplaceTeamSearchJSON }>({
      query: ({ query }) => ({
        url: `marketplace/teams?${MarketplaceTeamSearchSchema.getSearchParams(query).toString()}`,
      }),
      rawResponseSchema: z.object({ hits: z.array(TeamSchema.TeamJSONZ).optional() }).transform((r) => r.hits ?? []),
      onSchemaFailure: logSchemaFailure,
      providesTags: [{ type: "Team" as const, id: "LIST" }],
    }),

    getTeamAvatar: build.query<string, { teamId: string; size?: AvatarSize }>({
      query: ({ teamId, size }) => ({
        url: `marketplace/teams/${teamId}/avatar`,
        params: size ? { size } : undefined,
      }),
      providesTags: (_result, _error, { teamId }) => [{ type: "Team" as const, id: teamId }],
    }),

    getTeamInvitationByInvitationId: build.query<TeamInvitationJSON, { invitationId: string }>({
      query: ({ invitationId }) => `marketplace/teams/invitations/${invitationId}`,
      rawResponseSchema: TeamInvitationSchema.TeamInvitationJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, { invitationId }) => [{ type: "Team" as const, id: `invitation-${invitationId}` }],
    }),

    getTeamInvitationsByTeamId: build.query<TeamInvitationJSON[], { teamId: string; open?: boolean }>({
      query: ({ teamId, open = true }) => `marketplace/teams/invitations?teamId=${teamId}&open=${open}`,
      rawResponseSchema: z.array(TeamInvitationSchema.TeamInvitationJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, { teamId }) => [{ type: "Team" as const, id: `invitations-${teamId}` }],
    }),

    /**
     * The teams the signed-in viewer may put somebody onto, decided server-side.
     *
     * `inviteeId` is the individual being considered, and the answer says per team whether they
     * already belong. It is not a filter the client could apply itself: the API answers with the
     * same authority that the invitation command asserts, so an empty list is the honest reason the
     * Invite to Team action does not appear at all.
     *
     * Tagged with the team invitation lists it depends on, so accepting or cancelling an invitation
     * and then reopening a profile does not offer a stale answer.
     */
    getTeamsOpenToMyInvitations: build.query<TeamOpenToInvitationJSON[], { inviteeId?: string }>({
      query: ({ inviteeId }) =>
        inviteeId
          ? `marketplace/teams/open-to-my-invitations?inviteeId=${inviteeId}`
          : "marketplace/teams/open-to-my-invitations",
      rawResponseSchema: z.array(TeamOpenToInvitationSchema.TeamOpenToInvitationJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) => [
        { type: "Team" as const, id: "LIST" },
        ...(result ?? []).map((team) => ({ type: "Team" as const, id: `invitations-${team.id}` })),
      ],
    }),
    // #endregion

    // #region Mutations
    createTeam: build.mutation<TeamJSON, { team: Team }>({
      query: ({ team }) => ({
        url: "marketplace/teams",
        method: "POST",
        body: MarketplaceTeamAPIRequestSchema.serialize(team),
        headers: { "Content-Type": "application/json" },
      }),
      rawResponseSchema: TeamSchema.TeamJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [{ type: "Team" as const, id: "LIST" }],
    }),

    updateTeam: build.mutation<TeamJSON, { teamId: string; originalTeam: Team; updatedTeam: Team }>({
      query: ({ teamId, originalTeam, updatedTeam }) => ({
        url: `marketplace/teams/${teamId}`,
        method: "PATCH",
        body: buildUpdateMarketplaceTeamPayload(originalTeam, updatedTeam),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: TeamSchema.TeamJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { teamId }) => [{ type: "Team" as const, id: teamId }],
    }),

    deleteTeam: build.mutation<void, { teamId: string }>({
      query: ({ teamId }) => ({
        url: `marketplace/teams/${teamId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Team" as const, id: "LIST" }],
    }),

    createTeamAvatar: build.mutation<string, { teamId: string; file: File; crop?: AvatarCrop }>({
      query: ({ teamId, file, crop }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (crop) {
          formData.append("cropX", crop.x.toString());
          formData.append("cropY", crop.y.toString());
          formData.append("cropSize", crop.size.toString());
        }
        return {
          url: `marketplace/teams/${teamId}/avatar`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { teamId }) => [{ type: "Team" as const, id: teamId }],
    }),

    deleteTeamAvatar: build.mutation<void, { teamId: string }>({
      query: ({ teamId }) => ({
        url: `marketplace/teams/${teamId}/avatar`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { teamId }) => [{ type: "Team" as const, id: teamId }],
    }),

    requestToJoinTeam: build.mutation<void, { teamId: string }>({
      query: ({ teamId }) => ({
        url: `marketplace/teams/${teamId}/request-to-join`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (_result, _error, { teamId }) => [
        { type: "Team" as const, id: teamId },
        { type: "Team" as const, id: "LIST" },
      ],
    }),

    promoteTeamMemberToLeader: build.mutation<void, { teamId: string; individualId: string }>({
      query: ({ teamId, individualId }) => ({
        url: `marketplace/teams/${teamId}/leader`,
        method: "PUT",
        body: { individualId },
      }),
      invalidatesTags: (_result, _error, { teamId }) => [{ type: "Team" as const, id: teamId }],
    }),

    removeTeamMember: build.mutation<void, { teamId: string; memberIndividualId: string }>({
      query: ({ teamId, memberIndividualId }) => ({
        url: `marketplace/teams/${teamId}/members/remove-by-individual-id`,
        method: "POST",
        body: { individualId: memberIndividualId },
      }),
      invalidatesTags: (_result, _error, { teamId }) => [{ type: "Team" as const, id: teamId }],
    }),

    cancelTeamInvitation: build.mutation<void, { invitationId: string }>({
      query: ({ invitationId }) => ({
        url: `marketplace/teams/invitations/${invitationId}/cancel`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (_result, _error, { invitationId }) => [
        { type: "Team" as const, id: `invitation-${invitationId}` },
      ],
    }),

    acceptTeamInvitation: build.mutation<void, { invitationId: string; teamId: string }>({
      query: ({ invitationId }) => ({
        url: `marketplace/teams/invitations/${invitationId}/accept`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (_result, _error, { invitationId, teamId }) => [
        { type: "Team" as const, id: `invitation-${invitationId}` },
        { type: "Team" as const, id: `invitations-${teamId}` },
        { type: "Team" as const, id: teamId },
        { type: "Team" as const, id: "LIST" },
      ],
    }),

    rejectTeamInvitation: build.mutation<void, { invitationId: string; teamId: string }>({
      query: ({ invitationId }) => ({
        url: `marketplace/teams/invitations/${invitationId}/reject`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (_result, _error, { invitationId, teamId }) => [
        { type: "Team" as const, id: `invitation-${invitationId}` },
        { type: "Team" as const, id: `invitations-${teamId}` },
        { type: "Team" as const, id: teamId },
        { type: "Team" as const, id: "LIST" },
      ],
    }),

    createTeamInvitation: build.mutation<TeamInvitationJSON, { inviteeId: string; teamId: string; message?: string }>({
      query: ({ inviteeId, teamId, message }) => ({
        url: "marketplace/teams/invitations",
        method: "POST",
        body: { inviteeId, teamId, message },
        headers: { "Content-Type": "application/json" },
      }),
      rawResponseSchema: TeamInvitationSchema.TeamInvitationJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { teamId }) => [{ type: "Team" as const, id: `invitations-${teamId}` }],
    }),

    contactTeam: build.mutation<ForumJSON, { teamId: string }>({
      query: ({ teamId }) => ({
        url: `marketplace/teams/${teamId}/contact`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: ForumSchema.ForumJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { teamId }) => [{ type: "Team" as const, id: teamId }],
    }),
    // #endregion
  }),
});

export const {
  useGetTeamVendorsQuery,
  useGetTeamByIdQuery,
  useGetTeamsByIdsQuery,
  useSearchTeamsByQueryQuery,
  useGetTeamAvatarQuery,
  useGetTeamInvitationByInvitationIdQuery,
  useGetTeamInvitationsByTeamIdQuery,
  useGetTeamsOpenToMyInvitationsQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useCreateTeamAvatarMutation,
  useDeleteTeamAvatarMutation,
  useRequestToJoinTeamMutation,
  usePromoteTeamMemberToLeaderMutation,
  useRemoveTeamMemberMutation,
  useCancelTeamInvitationMutation,
  useAcceptTeamInvitationMutation,
  useRejectTeamInvitationMutation,
  useCreateTeamInvitationMutation,
  useContactTeamMutation,
} = marketplaceTeamsApi;
